import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Trash2, Pencil, Check, X, KeyRound, UserCog } from 'lucide-react';
import { del, put, post } from '@/shared/api/api_client';
import type { FamilyMember } from '@/entities/user';
import Badge from '@/shared/ui/Badge/Badge';
import Button from '@/shared/ui/Button/Button';
import { cn } from '@/shared/utils/cn';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface ManageMembersProps {
  familyId: string;
  members: FamilyMember[];
  currentUserId: string;
  isPrimaryParent: boolean;
}

// ---------------------------------------------------------------------------
// PIN input helpers
// ---------------------------------------------------------------------------
const PIN_REGEX = /^\d{4,6}$/;

function validatePin(pin: string, confirmPin: string): string | null {
  if (!PIN_REGEX.test(pin)) return 'PIN must be 4–6 digits';
  if (pin !== confirmPin) return 'PINs do not match';
  return null;
}

// ---------------------------------------------------------------------------
// Individual member row
// ---------------------------------------------------------------------------
interface MemberRowProps {
  member: FamilyMember;
  familyId: string;
  currentUserId: string;
  isPrimaryParent: boolean;
  onMutated: () => void;
}

type EditTab = 'profile' | 'login';

function MemberRow({
  member,
  familyId,
  currentUserId,
  isPrimaryParent,
  onMutated,
}: MemberRowProps): React.ReactElement {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTab, setEditTab] = useState<EditTab>('profile');

  // Profile tab state
  const [editName, setEditName] = useState(member.displayName);

  // Login tab state
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [canChangePin, setCanChangePin] = useState(member.canChangePin);
  const [pinError, setPinError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const isSelf = member.memberId === currentUserId;
  const isChild = member.role === 'child';

  const resetEditState = () => {
    setEditName(member.displayName);
    setPin('');
    setConfirmPin('');
    setShowPin(false);
    setShowConfirmPin(false);
    setCanChangePin(member.canChangePin);
    setPinError(null);
    setEditError(null);
    setEditTab('profile');
  };

  const removeMutation = useMutation({
    mutationFn: () => del<void>(`/families/${familyId}/members/${member.memberId}`),
    onSuccess: onMutated,
  });

  const editMutation = useMutation({
    mutationFn: () =>
      put<void>(`/families/${familyId}/members/${member.memberId}`, {
        displayName: editName.trim(),
      }),
    onSuccess: () => {
      setEditing(false);
      setEditError(null);
      onMutated();
    },
    onError: (err) => {
      setEditError(err instanceof Error ? err.message : 'Update failed.');
    },
  });

  const enableLoginMutation = useMutation({
    mutationFn: (data: { pin: string; canChangePin: boolean }) =>
      post<void>(`/auth/members/${member.memberId}/enable-login`, data),
    onSuccess: () => {
      setEditing(false);
      setPinError(null);
      setPin('');
      setConfirmPin('');
      onMutated();
    },
    onError: (err) => {
      setPinError(err instanceof Error ? err.message : 'Failed to update login.');
    },
  });

  const disableLoginMutation = useMutation({
    mutationFn: () => del<void>(`/auth/members/${member.memberId}/disable-login`),
    onSuccess: () => {
      setEditing(false);
      onMutated();
    },
    onError: (err) => {
      setPinError(err instanceof Error ? err.message : 'Failed to disable login.');
    },
  });

  const handlePinSubmit = () => {
    const err = validatePin(pin, confirmPin);
    if (err) {
      setPinError(err);
      return;
    }
    setPinError(null);
    enableLoginMutation.mutate({ pin, canChangePin });
  };

  const roleVariant = member.role === 'parent' ? 'info' : 'success';

  return (
    <li className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start gap-3">
        {/* Avatar initial */}
        <div
          aria-hidden="true"
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white',
            member.role === 'parent' ? 'bg-primary-500' : 'bg-secondary-500',
          )}
        >
          {member.displayName.charAt(0).toUpperCase()}
        </div>

        {/* Info */}
        <div className="flex flex-1 flex-col gap-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-slate-900 truncate">{member.displayName}</p>
            <Badge variant={roleVariant}>
              {member.role === 'parent' ? (member.isPrimary ? 'Primary Parent' : 'Parent') : 'Child'}
            </Badge>
            {isSelf && <Badge variant="neutral">You</Badge>}
          </div>
          {isChild && (
            <p className="text-xs text-slate-500">
              {member.hasPin ? 'Can log in' : 'No login set up'}
            </p>
          )}
        </div>

        {/* Actions */}
        {!isSelf && isPrimaryParent && !confirmRemove && !editing && (
          <div className="flex shrink-0 gap-2">
            {isChild && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setEditing(true)}
                leftIcon={<Pencil size={13} aria-hidden="true" />}
                aria-label={`Edit ${member.displayName}`}
              >
                Edit
              </Button>
            )}
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={() => setConfirmRemove(true)}
              leftIcon={<Trash2 size={13} aria-hidden="true" />}
              aria-label={`Remove ${member.displayName}`}
            >
              Remove
            </Button>
          </div>
        )}
      </div>

      {/* Confirm removal */}
      {confirmRemove && (
        <div className="rounded-lg bg-danger-50 px-3 py-2">
          <p className="text-sm text-danger-700 mb-2">
            Remove <strong>{member.displayName}</strong> from the family? This cannot be undone.
          </p>
          {removeMutation.isError && (
            <p role="alert" className="text-xs text-danger-600 mb-2">
              {removeMutation.error instanceof Error ? removeMutation.error.message : 'Removal failed.'}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setConfirmRemove(false)}
              disabled={removeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              variant="danger"
              isLoading={removeMutation.isPending}
              onClick={() => removeMutation.mutate()}
            >
              Yes, remove
            </Button>
          </div>
        </div>
      )}

      {/* Edit panel (children only) */}
      {editing && (
        <div className="flex flex-col gap-0 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
          {/* Tab bar */}
          <div className="flex border-b border-slate-200">
            <button
              type="button"
              onClick={() => setEditTab('profile')}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-colors',
                editTab === 'profile'
                  ? 'border-b-2 border-primary-600 bg-white text-primary-700'
                  : 'text-slate-500 hover:text-slate-700',
              )}
            >
              <UserCog size={13} aria-hidden="true" />
              Profile
            </button>
            <button
              type="button"
              onClick={() => { setEditTab('login'); setPinError(null); }}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-colors',
                editTab === 'login'
                  ? 'border-b-2 border-primary-600 bg-white text-primary-700'
                  : 'text-slate-500 hover:text-slate-700',
              )}
            >
              <KeyRound size={13} aria-hidden="true" />
              Login
              {member.hasPin && (
                <span className="ml-1 inline-flex items-center rounded-full bg-secondary-100 px-1.5 py-0.5 text-[10px] font-semibold text-secondary-700">
                  Active
                </span>
              )}
            </button>
          </div>

          <div className="p-3">
            {/* ---- Profile tab ---- */}
            {editTab === 'profile' && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor={`edit-name-${member.memberId}`}
                    className="text-xs font-medium text-slate-600"
                  >
                    Display name
                  </label>
                  <input
                    id={`edit-name-${member.memberId}`}
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    maxLength={80}
                    className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                {editError && (
                  <p role="alert" className="text-xs text-danger-600">{editError}</p>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => { setEditing(false); resetEditState(); }}
                    leftIcon={<X size={13} aria-hidden="true" />}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    isLoading={editMutation.isPending}
                    onClick={() => editMutation.mutate()}
                    leftIcon={<Check size={13} aria-hidden="true" />}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}

            {/* ---- Login tab ---- */}
            {editTab === 'login' && (
              <div className="flex flex-col gap-3">
                {!member.hasPin ? (
                  /* Enable login */
                  <>
                    <p className="text-xs text-slate-500">
                      {member.firstName} has no PIN yet. Set one so they can log in with the family code + their first name + this PIN.
                    </p>
                    <PinFields
                      memberId={member.memberId}
                      label="New PIN"
                      pin={pin}
                      confirmPin={confirmPin}
                      showPin={showPin}
                      showConfirmPin={showConfirmPin}
                      onPinChange={(v) => { setPin(v); setPinError(null); }}
                      onConfirmPinChange={(v) => { setConfirmPin(v); setPinError(null); }}
                      onTogglePin={() => setShowPin((p) => !p)}
                      onToggleConfirmPin={() => setShowConfirmPin((p) => !p)}
                    />
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                      <input
                        type="checkbox"
                        checked={canChangePin}
                        onChange={(e) => setCanChangePin(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      Allow {member.firstName} to change their own PIN
                    </label>
                    {pinError && (
                      <p role="alert" className="text-xs text-danger-600">{pinError}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => { setEditing(false); resetEditState(); }}
                        leftIcon={<X size={13} aria-hidden="true" />}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        isLoading={enableLoginMutation.isPending}
                        onClick={handlePinSubmit}
                        leftIcon={<Check size={13} aria-hidden="true" />}
                      >
                        Enable Login
                      </Button>
                    </div>
                  </>
                ) : (
                  /* Change / disable login */
                  <>
                    <p className="text-xs text-slate-500">
                      {member.firstName} can currently log in. You can change their PIN or disable login.
                    </p>
                    <PinFields
                      memberId={member.memberId}
                      label="New PIN"
                      pin={pin}
                      confirmPin={confirmPin}
                      showPin={showPin}
                      showConfirmPin={showConfirmPin}
                      onPinChange={(v) => { setPin(v); setPinError(null); }}
                      onConfirmPinChange={(v) => { setConfirmPin(v); setPinError(null); }}
                      onTogglePin={() => setShowPin((p) => !p)}
                      onToggleConfirmPin={() => setShowConfirmPin((p) => !p)}
                    />
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                      <input
                        type="checkbox"
                        checked={canChangePin}
                        onChange={(e) => setCanChangePin(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      Allow {member.firstName} to change their own PIN
                    </label>
                    {pinError && (
                      <p role="alert" className="text-xs text-danger-600">{pinError}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => { setEditing(false); resetEditState(); }}
                        leftIcon={<X size={13} aria-hidden="true" />}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        isLoading={enableLoginMutation.isPending}
                        disabled={disableLoginMutation.isPending}
                        onClick={handlePinSubmit}
                        leftIcon={<Check size={13} aria-hidden="true" />}
                      >
                        Change PIN
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        isLoading={disableLoginMutation.isPending}
                        disabled={enableLoginMutation.isPending}
                        onClick={() => disableLoginMutation.mutate()}
                      >
                        Disable Login
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Shared PIN fields sub-component
// ---------------------------------------------------------------------------
interface PinFieldsProps {
  memberId: string;
  label: string;
  pin: string;
  confirmPin: string;
  showPin: boolean;
  showConfirmPin: boolean;
  onPinChange: (v: string) => void;
  onConfirmPinChange: (v: string) => void;
  onTogglePin: () => void;
  onToggleConfirmPin: () => void;
}

function PinFields({
  memberId,
  label,
  pin,
  confirmPin,
  showPin,
  showConfirmPin,
  onPinChange,
  onConfirmPinChange,
  onTogglePin,
  onToggleConfirmPin,
}: PinFieldsProps): React.ReactElement {
  return (
    <>
      <div className="flex flex-col gap-1">
        <label htmlFor={`pin-${memberId}`} className="text-xs font-medium text-slate-600">
          {label}
        </label>
        <div className="relative">
          <input
            id={`pin-${memberId}`}
            type={showPin ? 'text' : 'password'}
            inputMode="numeric"
            maxLength={6}
            placeholder="••••••"
            autoComplete="new-password"
            value={pin}
            onChange={(e) => onPinChange(e.target.value.replace(/\D/g, ''))}
            className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 pr-9 text-xl tracking-[0.4em] text-slate-900 placeholder:text-slate-300 placeholder:tracking-normal focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            type="button"
            aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
            onClick={onTogglePin}
            className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400 hover:text-slate-600"
          >
            {showPin ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
          </button>
        </div>
        <p className="text-[10px] text-slate-400">4–6 digits</p>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor={`confirm-pin-${memberId}`} className="text-xs font-medium text-slate-600">
          Confirm PIN
        </label>
        <div className="relative">
          <input
            id={`confirm-pin-${memberId}`}
            type={showConfirmPin ? 'text' : 'password'}
            inputMode="numeric"
            maxLength={6}
            placeholder="••••••"
            autoComplete="new-password"
            value={confirmPin}
            onChange={(e) => onConfirmPinChange(e.target.value.replace(/\D/g, ''))}
            className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 pr-9 text-xl tracking-[0.4em] text-slate-900 placeholder:text-slate-300 placeholder:tracking-normal focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            type="button"
            aria-label={showConfirmPin ? 'Hide confirm PIN' : 'Show confirm PIN'}
            onClick={onToggleConfirmPin}
            className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400 hover:text-slate-600"
          >
            {showConfirmPin ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
function ManageMembers({
  familyId,
  members,
  currentUserId,
  isPrimaryParent,
}: ManageMembersProps): React.ReactElement {
  const queryClient = useQueryClient();

  const handleMutated = () => {
    queryClient.invalidateQueries({ queryKey: ['members', familyId] });
  };

  const parents = members.filter((m) => m.role === 'parent');
  const children = members.filter((m) => m.role === 'child');

  return (
    <div className="flex flex-col gap-6">
      {/* Parents */}
      {parents.length > 0 && (
        <section aria-labelledby="parents-heading">
          <h2 id="parents-heading" className="mb-3 text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Parents
          </h2>
          <ul className="flex flex-col gap-3" aria-label="Parent members">
            {parents.map((member) => (
              <MemberRow
                key={member.memberId}
                member={member}
                familyId={familyId}
                currentUserId={currentUserId}
                isPrimaryParent={isPrimaryParent}
                onMutated={handleMutated}
              />
            ))}
          </ul>
        </section>
      )}

      {/* Children */}
      {children.length > 0 && (
        <section aria-labelledby="children-heading">
          <h2 id="children-heading" className="mb-3 text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Children
          </h2>
          <ul className="flex flex-col gap-3" aria-label="Child members">
            {children.map((member) => (
              <MemberRow
                key={member.memberId}
                member={member}
                familyId={familyId}
                currentUserId={currentUserId}
                isPrimaryParent={isPrimaryParent}
                onMutated={handleMutated}
              />
            ))}
          </ul>
        </section>
      )}

      {members.length === 0 && (
        <p className="text-sm text-slate-400">No family members found.</p>
      )}
    </div>
  );
}

export default ManageMembers;
